import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Alert,
  Stack,
  Divider,
  LinearProgress,
  Avatar,
  Chip
} from "@mui/material";
import { CloudUpload, Delete, CheckCircle } from '@mui/icons-material';
import { vendorApi } from "../../api/api";
import { useSelector } from "react-redux";
import GradientButton from "../common/GradiantButton";
import SecondaryButton from "../common/SecondaryButton";
const AddImages = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const theme = useSelector((data) => data.themeValue.colors);
  const propertyData = state?.propertyData;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 10) {
      setUploadStatus('error');
      return;
    }
    setUploadStatus(null);
    setSelectedFiles([...selectedFiles, ...files]);
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages([...previewImages, ...previews]);
  };
  const removeImage = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewImages];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewImages(newPreviews);
  };
  const handleUpload = async () => {
    if (!selectedFiles.length) {
      setUploadStatus('error');
      return;
    }
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("Images[]", file);
    });
    const property = Array.isArray(propertyData) ? propertyData[0] : propertyData;
    formData.append("id", property?.id ?? "");
    formData.append("type", property?.type ?? "");
    setUploadStatus('uploading');
    setUploadProgress(0);
    try {
      const response = await vendorApi.addImagesById(formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      if (response.status === 200) {
        setUploadStatus('success');
        setTimeout(() => navigate(-1), 1500);
      } else {
        setUploadStatus('error');
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus('error');
    }
  };
  return (
    <Paper sx={{
      p: 4,
      borderRadius: 2,
      width: "100%",
      marginTop: "30px",
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
      alignItems: "center",
      alignContent: "center",
      backgroundColor: theme.background
    }}>
      <Box sx={{ width: "50%" }}>
        <Typography variant="h5" sx={{
          mb: 3,
          fontWeight: 600,
          color: theme.text,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CloudUpload fontSize="large" sx={{ color: theme.normal_icon }} />
          Upload Property Images
        </Typography>
        <Divider sx={{ mb: 3, backgroundColor: theme.secondary_text_color }} />
        {/* Upload Area */}
        <Box sx={{
          border: '2px dashed',
          borderColor: 'primary.light',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          mb: 3,
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)'
          }
        }}>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="property-image-upload"
          />
          <label htmlFor="property-image-upload">
            <Button
              variant="outlined"
              color="primary"
              component="span"
              startIcon={<CloudUpload />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Browse Files
            </Button>
          </label>
          <Typography variant="body2" sx={{ mt: 2, color: theme.text }}>
            {selectedFiles.length > 0
              ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected (max 10)`
              : 'Drag & drop images here or click to browse'}
          </Typography>
        </Box>
        {/* Status Messages */}
        {uploadStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {selectedFiles.length === 0
              ? "Please select images to upload."
              : selectedFiles.length > 10
                ? "You can upload a maximum of 10 images"
                : "Upload failed. Please try again."}
          </Alert>
        )}
        {uploadStatus === 'uploading' && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Uploading: {uploadProgress}%
            </Typography>
          </Box>
        )}
        {uploadStatus === 'success' && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            icon={<CheckCircle fontSize="inherit" />}
          >
            Images uploaded successfully!
          </Alert>
        )}
        {/* Selected Files Preview */}
        {previewImages.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Selected Images ({selectedFiles.length}/10)
            </Typography>
            <Stack spacing={2}>
              {previewImages.map((src, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={src}
                    alt={`preview-${index}`}
                    sx={{
                      width: 80,
                      height: 80,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" noWrap sx={{ fontWeight: 500 }}>
                      {selectedFiles[index].name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedFiles[index].size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                  <Chip
                    label="Ready to upload"
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    onClick={() => removeImage(index)}
                    size="small"
                    sx={{ color: 'error.main' }}
                  >
                    <Delete />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
        {/* Action Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <GradientButton 
          text={uploadStatus === 'uploading' ? 'Uploading...' : `Upload ${selectedFiles.length} Image${selectedFiles.length > 1 ? 's' : ''}`}
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploadStatus === 'uploading'}
          width={{lg:"30%",xs:"100%",md:"60%",}}/>
    

          {selectedFiles.length > 0 && (
            <SecondaryButton text="Clear All"
            onClick={() => {
                setSelectedFiles([]);
                setPreviewImages([]);
              }}
              disabled={uploadStatus === 'uploading'}
              width="40%"/>
            
          )}
        </Stack>
      </Box>
    </Paper>
  );
};
export default AddImages;









